import { ExternalLink as ExternalLinkIcon, Inbox, type LucideIcon, RefreshCw } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useId,
} from "react";
import { errorMessage } from "../lib/http";

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

// --- Button ---------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

const buttonVariants: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-fg hover:bg-accent-hover border border-transparent shadow-sm",
  secondary: "bg-surface text-fg border border-line-strong hover:bg-subtle shadow-sm",
  danger: "bg-surface text-danger border border-line-strong hover:bg-danger-subtle shadow-sm",
  ghost: "text-fg-muted hover:text-fg hover:bg-subtle border border-transparent",
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-[0.8125rem]",
  md: "h-8 px-3 text-sm",
  lg: "h-10 px-4 text-sm",
};

/** Button styling for elements that are not buttons, such as links that look like one. */
export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "md"): string {
  return cx(
    "inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors",
    "[&_svg]:size-4 [&_svg]:shrink-0",
    buttonVariants[variant],
    buttonSizes[size],
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(buttonClass(variant, size), "disabled:cursor-not-allowed disabled:opacity-60", className)}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

/** Only used inside buttons; page and section loading states are skeletons. */
function Spinner() {
  return (
    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// --- Skeleton --------------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cx("block animate-pulse rounded bg-subtle", className)} />;
}

/** A labelled block of skeleton lines for whole sections that are still loading. */
export function SkeletonBlock({ label = "Loading", lines = 3 }: { label?: string; lines?: number }) {
  return (
    <output className="block space-y-3 py-2" aria-label={label}>
      {Array.from({ length: lines }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <Skeleton key={i} className={cx("h-4", i % 3 === 2 ? "w-2/5" : i % 2 ? "w-3/4" : "w-full")} />
      ))}
    </output>
  );
}

// --- Layout ---------------------------------------------------------------------

export function PageHeader({
  title,
  description,
  actions,
  children,
}: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[1.375rem] font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/**
 * Bordered white box with an optional header row. `flush` drops the body padding for content
 * that runs edge to edge and pads itself, such as tables and row lists.
 */
export function Card({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
  flush,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  flush?: boolean;
}) {
  return (
    <section className={cx("rounded-lg border border-line bg-surface shadow-xs", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sm:flex-nowrap sm:px-5">
          <div className="min-w-0 flex-1">
            {title && <h2 className="text-[0.9375rem] font-semibold">{title}</h2>}
            {description && <p className="mt-0.5 text-[0.8125rem] text-fg-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cx(!flush && "px-4 py-4 sm:px-5", bodyClassName)}>{children}</div>
    </section>
  );
}

/** Unboxed page section: a heading over a hairline, as on detail pages. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      <header className="mb-3 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-2">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {description && <p className="mt-0.5 text-[0.8125rem] text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {children}
    </section>
  );
}

// --- Alerts ------------------------------------------------------------------------

type AlertTone = "info" | "warn" | "danger" | "ok";

const alertTones: Record<AlertTone, string> = {
  info: "border-line bg-subtle text-fg",
  warn: "border-warn/30 bg-warn-subtle text-warn",
  danger: "border-danger/30 bg-danger-subtle text-danger",
  ok: "border-ok/30 bg-ok-subtle text-ok",
};

export function Alert({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cx("rounded-md border px-3.5 py-2.5 text-[0.8125rem]", alertTones[tone], className)}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={cx(title ? "mt-0.5" : undefined, "[&_a]:underline")}>{children}</div>}
    </div>
  );
}

export function ErrorAlert({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  return (
    <Alert tone="danger" title={title}>
      {errorMessage(error)}
    </Alert>
  );
}

/** A failed load, with a way to try again (e.g. a query's `refetch`). */
export function QueryError({
  error,
  title,
  onRetry,
  retrying,
}: {
  error: unknown;
  title: string;
  onRetry: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="space-y-3">
      <ErrorAlert error={error} title={title} />
      <Button variant="secondary" size="sm" onClick={onRetry} loading={retrying}>
        <RefreshCw aria-hidden="true" /> Try again
      </Button>
    </div>
  );
}

// --- Form fields ---------------------------------------------------------------------

const controlClass =
  "block w-full rounded-md border border-line-strong bg-surface px-2.5 text-sm text-fg shadow-xs placeholder:text-fg-subtle " +
  "focus:border-ring focus:outline-2 focus:outline-offset-0 focus:outline-ring/30 " +
  "disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-danger";

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: (props: { id: string; "aria-describedby"?: string; "aria-invalid"?: boolean }) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[0.8125rem] font-medium">
        {label}
      </label>
      {children({ id, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined })}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-fg-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(controlClass, "h-8", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(controlClass, "h-8 pr-8", className)} {...rest}>
      {children}
    </select>
  );
}

/** Small radio group rendered as joined buttons (e.g. the 7 / 30 / 90 day range). */
export function SegmentedControl<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  const name = useId();
  return (
    <fieldset className="inline-flex rounded-md border border-line-strong bg-surface p-0.5 shadow-xs">
      <legend className="sr-only">{label}</legend>
      {options.map((o) => (
        <label
          key={String(o.value)}
          className={cx(
            "cursor-pointer rounded px-2.5 py-0.5 text-[0.8125rem] font-medium has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring",
            value === o.value ? "bg-subtle text-fg" : "text-fg-muted hover:text-fg",
          )}
        >
          <input
            type="radio"
            name={name}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="sr-only"
          />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

// --- Misc ------------------------------------------------------------------------------

export function EmptyState({
  title,
  children,
  icon: Icon = Inbox,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <div className="px-4 py-10 text-center">
      <span className="mx-auto flex size-9 items-center justify-center rounded-md bg-subtle text-fg-muted">
        <Icon aria-hidden="true" className="size-4.5" />
      </span>
      <p className="mt-3 text-sm font-medium">{title}</p>
      {children && <div className="mx-auto mt-1 max-w-sm text-[0.8125rem] text-fg-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Mono({
  children,
  className,
  title,
}: { children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cx("font-mono text-[0.8125rem] break-all", className)}>
      {children}
    </span>
  );
}

/** Label / value rows: a label column (two fifths) on the left from `sm` up. */
const DESCRIPTION_ROW =
  "grid gap-1 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4";

export function DescriptionList({ items }: { items: Array<{ label: ReactNode; value: ReactNode }> }) {
  return (
    <dl className="divide-y divide-line text-[0.8125rem]">
      {items.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list
        <div key={i} className={DESCRIPTION_ROW}>
          <dt className="text-fg-muted">{item.label}</dt>
          <dd className="min-w-0 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ExternalLink({
  href,
  children,
  className,
}: { href: string; children: ReactNode; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cx(
        "inline-flex min-w-0 items-center gap-1 rounded text-accent underline-offset-2 hover:underline",
        className,
      )}
    >
      {children}
      <ExternalLinkIcon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

/** An em dash for values that are not set. */
export function None() {
  return (
    <span className="text-fg-subtle">
      <span aria-hidden="true">—</span>
      <span className="sr-only">None</span>
    </span>
  );
}
